import { type ReactElement, type ReactNode } from 'react';
import { Box, Group, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { Check } from 'lucide-react';
import { useThemeContext, type ThemeMode, type ThemeStyle } from '@/design/ThemeContext';
import { lightPalette, darkPalette } from '@/design/AppTheme';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';

const ACCENT = 'var(--bm-color-brand-accent)';

const MODE_PALETTE: Record<ThemeMode, typeof lightPalette> = {
    light: lightPalette,
    dark: darkPalette,
};

const MODE_OPTIONS: { value: ThemeMode; label: string; description: string }[] = [
    { value: 'light', label: 'Light', description: 'Bright, high-contrast interface.' },
    { value: 'dark', label: 'Dark', description: 'Dimmed for low-light environments.' },
];

const STYLE_OPTIONS: { value: ThemeStyle; label: string; description: string }[] = [
    { value: 'standard', label: 'Standard', description: 'Softer and more spacious.' },
    { value: 'operations', label: 'Operations', description: 'Sharp and dense.' },
];

function ModePreview({ value }: { value: ThemeMode }): ReactElement {
    const p = MODE_PALETTE[value];
    return (
        <Box
            style={{
                height: 96,
                borderRadius: 6,
                overflow: 'hidden',
                border: `1px solid ${p.border}`,
                display: 'flex',
                background: p.body,
            }}
        >
            <Box
                style={{
                    width: '34%',
                    background: p.surface,
                    borderRight: `1px solid ${p.border}`,
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                }}
            >
                <Box style={{ height: 6, width: '75%', background: ACCENT, borderRadius: 2 }} />
                <Box style={{ height: 4, width: '85%', background: p.border, borderRadius: 2 }} />
                <Box style={{ height: 4, width: '65%', background: p.border, borderRadius: 2 }} />
                <Box style={{ height: 4, width: '78%', background: p.border, borderRadius: 2 }} />
            </Box>
            <Box style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Box style={{ height: 7, width: '55%', background: p.text, borderRadius: 2 }} />
                <Box style={{ height: 4, width: '100%', background: p.border, borderRadius: 2 }} />
                <Box style={{ height: 4, width: '92%', background: p.border, borderRadius: 2 }} />
                <Box style={{ display: 'flex', gap: 5, marginTop: 'auto' }}>
                    <Box style={{ height: 12, width: 40, background: ACCENT, borderRadius: 3 }} />
                    <Box
                        style={{
                            height: 12,
                            width: 28,
                            background: p.surface,
                            border: `1px solid ${p.border}`,
                            borderRadius: 3,
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

function StylePreview({ value }: { value: ThemeStyle }): ReactElement {
    const radius = value === 'standard' ? 6 : 0;
    const innerRadius = value === 'standard' ? 2 : 0;
    const gap = value === 'standard' ? 7 : 3;
    return (
        <Box
            style={{
                height: 96,
                borderRadius: radius,
                border: '1px solid var(--bm-color-border)',
                background: 'var(--bm-color-surface)',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap,
                justifyContent: 'center',
            }}
        >
            <Box style={{ height: 7, width: '55%', background: 'var(--bm-color-text-dimmed)', borderRadius: innerRadius }} />
            <Box style={{ height: 5, width: '100%', background: 'var(--bm-color-border)', borderRadius: innerRadius }} />
            <Box style={{ height: 5, width: '88%', background: 'var(--bm-color-border)', borderRadius: innerRadius }} />
            <Box
                style={{
                    height: 16,
                    width: 56,
                    marginTop: 4,
                    background: 'var(--bm-color-brand-accent)',
                    borderRadius: value === 'standard' ? 3 : 0,
                }}
            />
        </Box>
    );
}

function ThemeOptionCard({
    selected,
    onSelect,
    label,
    description,
    preview,
}: {
    selected: boolean;
    onSelect: () => void;
    label: string;
    description: string;
    preview: ReactNode;
}): ReactElement {
    return (
        <UnstyledButton onClick={onSelect} aria-pressed={selected} style={{ display: 'block', width: '100%' }}>
            <Box
                style={{
                    border: `1px solid ${selected ? 'var(--bm-color-brand-accent)' : 'var(--bm-color-border)'}`,
                    boxShadow: selected ? 'inset 0 0 0 1px var(--bm-color-brand-accent)' : undefined,
                    borderRadius: 'var(--bm-radius)',
                    background: 'var(--bm-color-body)',
                    padding: 'var(--mantine-spacing-md)',
                }}
            >
                {preview}
                <Group justify="space-between" align="center" mt="sm" wrap="nowrap">
                    <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600}>
                            {label}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {description}
                        </Text>
                    </Box>
                    <Box
                        style={{
                            width: 18,
                            height: 18,
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${selected ? 'var(--bm-color-brand-accent)' : 'var(--bm-color-border)'}`,
                            background: selected ? 'var(--bm-color-brand-accent)' : 'transparent',
                        }}
                    >
                        {selected && <Check size={11} color="#fff" strokeWidth={3} />}
                    </Box>
                </Group>
            </Box>
        </UnstyledButton>
    );
}

export default function ThemeSettingsPage(): ReactElement {
    const { mode, style, setMode, setStyle } = useThemeContext();

    return (
        <Box>
            <PageHeaderComponent title="Theme" subtitle="Customize the appearance of your workspace." />

            <SurfaceCard $padding="lg" $static>
            <Stack gap="xl">
                <Box>
                    <Text size="sm" fw={600} mb="xs">
                        Appearance
                    </Text>
                    <Text size="xs" c="dimmed" mb="sm">
                        Switch between a light and dark interface.
                    </Text>
                    <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md" maw={520}>
                        {MODE_OPTIONS.map((o) => (
                            <ThemeOptionCard
                                key={o.value}
                                selected={mode === o.value}
                                onSelect={() => setMode(o.value)}
                                label={o.label}
                                description={o.description}
                                preview={<ModePreview value={o.value} />}
                            />
                        ))}
                    </SimpleGrid>
                </Box>

                <Box>
                    <Text size="sm" fw={600} mb="xs">
                        Style
                    </Text>
                    <Text size="xs" c="dimmed" mb="sm">
                        Standard is softer and more spacious. Operations is sharp and dense.
                    </Text>
                    <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md" maw={520}>
                        {STYLE_OPTIONS.map((o) => (
                            <ThemeOptionCard
                                key={o.value}
                                selected={style === o.value}
                                onSelect={() => setStyle(o.value)}
                                label={o.label}
                                description={o.description}
                                preview={<StylePreview value={o.value} />}
                            />
                        ))}
                    </SimpleGrid>
                </Box>
            </Stack>
            </SurfaceCard>
        </Box>
    );
}
